import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, Check, Mail, User as UserIcon, AlertTriangle, Bell, BellOff } from "lucide-react";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { api, auth } from "../lib/api";
import { toast } from "sonner";

/**
 * Settings — self-service profile management.
 *   • Name    — free text, saves instantly.
 *   • Email   — two-step: request → verification link → confirm.
 *     Prevents typos and hostile email swap-outs on stolen sessions.
 *
 * The confirm step is triggered by the `?verify_email=<token>` query
 * param on this page (link is emailed to the NEW address).
 */
export default function Settings() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [newEmail, setNewEmail] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [requestingEmail, setRequestingEmail] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [emailRequestedTo, setEmailRequestedTo] = useState(null);
  // Notifications: `email_opt_out=true` means alerts are OFF. Default ON.
  const [emailAlertsEnabled, setEmailAlertsEnabled] = useState(user?.email_opt_out !== true);
  const [savingNotif, setSavingNotif] = useState(false);
  const [params] = useSearchParams();
  const nav = useNavigate();

  useEffect(() => {
    setEmailAlertsEnabled(user?.email_opt_out !== true);
  }, [user?.email_opt_out]);

  const toggleEmailAlerts = async (next) => {
    setSavingNotif(true);
    // Optimistic UI — snap the toggle immediately, revert on error.
    setEmailAlertsEnabled(next);
    try {
      const r = await api.post("/settings/notifications", { email_alerts_enabled: next });
      if (r.data.user) {
        auth.setUser(r.data.user);
        setUser && setUser(r.data.user);
      }
      toast.success(next ? "Email alerts ON" : "Email alerts OFF");
    } catch (e) {
      setEmailAlertsEnabled(!next);
      toast.error(e?.response?.data?.detail || "Couldn't save preference");
    } finally {
      setSavingNotif(false);
    }
  };

  // Confirm-email flow — triggered by clicking the link in the verification email
  useEffect(() => {
    const token = params.get("verify_email");
    if (!token) return;
    setConfirming(true);
    api
      .post("/settings/email/confirm", { token })
      .then((r) => {
        if (r.data.access_token) auth.setToken(r.data.access_token);
        if (r.data.user) {
          auth.setUser(r.data.user);
          setUser && setUser(r.data.user);
        }
        toast.success("Email updated ✓");
        nav("/app/settings", { replace: true });
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Verification link invalid or expired.");
      })
      .finally(() => setConfirming(false));
  }, []);   

  useEffect(() => {
    setName(user?.name || "");
  }, [user?.name]);

  const saveName = async () => {
    if (!name.trim() || name.trim() === user?.name) return;
    setSavingName(true);
    try {
      const r = await api.post("/settings/name", { name: name.trim() });
      if (r.data.user) {
        auth.setUser(r.data.user);
        setUser && setUser(r.data.user);
      }
      toast.success("Name saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingName(false);
    }
  };

  const requestEmailChange = async () => {
    if (!newEmail.trim() || newEmail.trim().toLowerCase() === (user?.email || "").toLowerCase()) {
      toast.error("Enter a different email address.");
      return;
    }
    setRequestingEmail(true);
    try {
      const r = await api.post("/settings/email/request-change", { new_email: newEmail.trim() });
      setEmailRequestedTo(newEmail.trim());
      toast.success(r.data.message || "Verification email sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send verification email");
    } finally {
      setRequestingEmail(false);
    }
  };

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-10">
        <div className="label-uppercase text-mb-emerald">Profile</div>
        <h1 className="font-display font-semibold text-4xl tracking-tight mt-1" data-testid="settings-title">
          Settings
        </h1>
        <p className="text-neutral-600 mt-1 text-sm">Update your display name and login email.</p>

        {confirming && (
          <div className="mt-8 p-6 rounded-lg bg-white border border-mb-border flex items-center gap-3" data-testid="settings-confirming-banner">
            <Loader2 className="w-5 h-5 animate-spin text-mb-emerald" />
            <span className="text-sm">Confirming your new email…</span>
          </div>
        )}

        {/* Name */}
        <section className="mt-8 p-6 rounded-lg bg-white border border-mb-border" data-testid="settings-name-card">
          <div className="flex items-center gap-2 mb-1">
            <UserIcon className="w-4 h-4 text-mb-emerald" />
            <h2 className="font-display font-semibold text-lg">Display name</h2>
          </div>
          <p className="text-xs text-neutral-500 mb-4">
            Shown on your dashboard and inside email greetings.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="rounded-md"
              data-testid="settings-name-input"
            />
            <Button
              onClick={saveName}
              disabled={savingName || !name.trim() || name.trim() === user?.name}
              className="bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-md"
              data-testid="settings-name-save"
            >
              {savingName ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </section>

        {/* Email */}
        <section className="mt-6 p-6 rounded-lg bg-white border border-mb-border" data-testid="settings-email-card">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="w-4 h-4 text-mb-emerald" />
            <h2 className="font-display font-semibold text-lg">Login email</h2>
          </div>
          <p className="text-xs text-neutral-500 mb-4">
            Current: <span className="font-mono-data font-semibold text-neutral-900">{user?.email}</span>
          </p>
          {emailRequestedTo ? (
            <div className="p-4 rounded-md bg-mb-emerald/10 border border-mb-emerald/30">
              <div className="flex items-start gap-2">
                <Check className="w-5 h-5 text-mb-emerald flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-mb-emerald">Verification email sent</p>
                  <p className="text-xs text-neutral-600 mt-1">
                    We just emailed <span className="font-mono-data">{emailRequestedTo}</span> — click the link inside within 30 minutes to complete the change. Your email won&apos;t switch until you click it.
                  </p>
                  <button
                    onClick={() => setEmailRequestedTo(null)}
                    className="mt-2 text-xs underline text-neutral-500 hover:text-neutral-800"
                  >
                    Send another
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="new-email@example.com"
                  className="rounded-md"
                  data-testid="settings-email-input"
                />
                <Button
                  onClick={requestEmailChange}
                  disabled={requestingEmail || !newEmail.trim()}
                  className="bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-md"
                  data-testid="settings-email-request"
                >
                  {requestingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
                  Send verification link
                </Button>
              </div>
              <div className="mt-3 flex items-start gap-2 text-xs text-neutral-500">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  We send a confirmation link to the new address to make sure it&apos;s really you. Your login email doesn&apos;t change until you click the link.
                </span>
              </div>
            </>
          )}
        </section>

        {/* Notifications */}
        <section className="mt-6 p-6 rounded-lg bg-white border border-mb-border" data-testid="settings-notifications-card">
          <div className="flex items-center gap-2 mb-1">
            {emailAlertsEnabled
              ? <Bell className="w-4 h-4 text-mb-emerald" />
              : <BellOff className="w-4 h-4 text-neutral-500" />}
            <h2 className="font-display font-semibold text-lg">Notifications</h2>
          </div>
          <p className="text-xs text-neutral-500 mb-4">
            Email delivery for new BUY alerts and exit notifications. Discord + dashboard delivery are unaffected.
          </p>
          <label className="flex items-center gap-3 cursor-pointer" data-testid="settings-email-alerts-toggle">
            <input
              type="checkbox"
              checked={emailAlertsEnabled}
              onChange={(e) => toggleEmailAlerts(e.target.checked)}
              disabled={savingNotif}
              className="peer sr-only"
            />
            <span className={`w-11 h-6 rounded-full transition relative
              ${emailAlertsEnabled ? "bg-mb-emerald" : "bg-neutral-300"}
              peer-disabled:opacity-50`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition
                ${emailAlertsEnabled ? "translate-x-5" : "translate-x-0"}`} />
            </span>
            <span className="text-sm">
              <span className="font-semibold text-neutral-900">Email alerts</span>
              <span className="ml-2 text-neutral-500">
                {savingNotif ? "saving…" : emailAlertsEnabled ? "on" : "off"}
              </span>
            </span>
          </label>
        </section>
      </div>
    </div>
  );
}
